import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup
} from 'react-bootstrap';
import { FaSave, FaArrowLeft } from 'react-icons/fa';

const ProcessPayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [bill, setBill] = useState(null);
  
  // Payment form data
  const [paymentData, setPaymentData] = useState({
    billingId: id,
    amount: 0,
    paymentMethod: 'cash',
    paymentDate: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });
  
  // Fetch bill details
  useEffect(() => {
    const fetchBill = async () => {
      try {
        const res = await axios.get(`/api/billings.php?id=${id}`);
        setBill(res.data);
        
        // Set default amount to the remaining balance
        const remainingBalance = res.data.totalAmount - res.data.paidAmount;
        setPaymentData(prev => ({
          ...prev,
          amount: remainingBalance
        }));
        
        setError(null);
      } catch (err) {
        console.error('Error fetching bill:', err);
        setError('Failed to load bill. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBill();
  }, [id]);
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'amount') {
      // Ensure amount is not negative and not more than the remaining balance
      const remaining = bill ? bill.totalAmount - bill.paidAmount : 0;
      let amount = parseFloat(value);
      
      if (isNaN(amount)) {
        amount = 0;
      } else if (amount > remaining) {
        amount = remaining;
      } else if (amount < 0) {
        amount = 0;
      }
      
      setPaymentData({
        ...paymentData,
        [name]: amount
      });
    } else {
      setPaymentData({
        ...paymentData,
        [name]: value
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post('/api/payments.php', paymentData);
      setSuccess(true);
      
      // Redirect after successful payment
      setTimeout(() => {
        navigate(`/billing/${id}`);
      }, 1500);
    } catch (err) {
      console.error('Error processing payment:', err);
      setError(err.response?.data?.msg || 'Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  if (loading && !bill) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading bill data...</p>
      </div>
    );
  }
  
  if (!bill) {
    return <Alert variant="warning">Bill not found.</Alert>;
  }
  
  const remainingBalance = bill.totalAmount - bill.paidAmount;
  
  return (
    <div className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Process Payment</h2>
        <Button variant="outline-secondary" onClick={() => navigate(`/billing/${id}`)}>
          <FaArrowLeft className="me-2" /> Back to Bill
        </Button>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Payment processed successfully!</Alert>}
      
      <Row>
        <Col md={7}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Payment Details</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Amount</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>Rs.</InputGroup.Text>
                        <Form.Control
                          type="number"
                          name="amount"
                          value={paymentData.amount}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          max={remainingBalance}
                          required
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Maximum: {formatCurrency(remainingBalance)}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select
                        name="paymentMethod"
                        value={paymentData.paymentMethod}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Credit/Debit Card</option>
                        <option value="bank">Bank Transfer</option>
                        <option value="insurance">Insurance</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="paymentDate"
                        value={paymentData.paymentDate}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reference Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="reference"
                        value={paymentData.reference}
                        onChange={handleInputChange}
                        placeholder="Receipt/Transaction ID (Optional)"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="notes"
                    value={paymentData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Any notes about this payment (Optional)"
                  />
                </Form.Group>
                
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading || success || paymentData.amount <= 0}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" /> Process Payment
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={5}>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">Bill Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Bill #:</strong> {bill.billNumber || `BILL-${bill.id}`}
              </div>
              <div className="mb-3">
                <strong>Patient:</strong> {bill.Patient?.name}
              </div>
              <div className="mb-3">
                <strong>Date:</strong> {new Date(bill.createdAt).toLocaleDateString()}
              </div>
              <div className="mb-3">
                <strong>Total Amount:</strong> {formatCurrency(bill.totalAmount)}
              </div>
              <div className="mb-3">
                <strong>Already Paid:</strong> {formatCurrency(bill.paidAmount)}
              </div>
              <div className="mb-3">
                <strong>Balance Due:</strong>{' '}
                <span className={remainingBalance > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                  {formatCurrency(remainingBalance)}
                </span>
              </div>
              <div className="mb-3">
                <strong>Status:</strong> {bill.paymentStatus}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProcessPayment; 
